import { ReactNode, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { cx } from '@emotion/css';
import { useModal } from 'mui-modal-provider';

import { useMutation } from '@apollo/client';
import { useStyles } from './unsubscribe.styles';
import logo from '../../../../assets/images/logo/logo-blue.svg';
import littleMail from '../../../../assets/images/unsibscribe/little-mail.svg';
import { Button } from '../../../common/button-new';
import { Form } from './form';
import { SubmitModal } from './submit-modal';
import { ExternalLinks } from '../../../../constants';
import { IFormImperativeHandleProps } from '../../../common/form';
import { Page } from '../page/page';
import { UNSUBSCRIBE_CONSUMER } from '../../../../graphql/unsubscribe/unsubscribe-consumer-by-ticket';
import { useActionsInProgress } from '../../../../graphql/preloader/actions/actions-in-progress';
import { promiseErrorCallbacks } from '../../../../utils/promise/set-promise-error-callbacks';
import { useCancellablePromise } from '../../../../hooks/use-cancellable-promise/use-cancellable-promise';

export const Unsubscribe = (): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const { showModal } = useModal();
  const formRef = useRef<IFormImperativeHandleProps>(null);

  const [unsubscribeConsumer] = useMutation(UNSUBSCRIBE_CONSUMER);
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const onUnsubscribe = async (): Promise<void> => {
    const formValues = formRef?.current?.getValues();

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams?.get('token');

    if (!token) {
      return;
    }

    const reasons: string[] = (formValues as any)?.unsubscribeFormOptions || [];
    const otherValue = (formValues as unknown as any)?.other;
    const comment: string = typeof otherValue === 'string' ? otherValue.trim() : '';

    if (!reasons.length && !comment) {
      return;
    }

    try {
      addActionInProgress();
      await makeCancellablePromise(
        unsubscribeConsumer({
          variables: {
            unsubscriptionInfo: {
              authenticationTicket: token,
              unsubscriptionComment: comment,
              unsubscriptionReason: reasons,
            },
          },
        }),
      );

      showModal(SubmitModal, { isCancel: false });
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }

    } finally {
      removeActionInProgress();
    }
  };

  const onCancel = (): void => {
    showModal(SubmitModal, { isCancel: true });
  };

  return (
    <Page title={intl.formatMessage({ id: 'unsubscribe.title' })}>
      <div className={classes.root}>
        <div className={classes.envelope}>
          <h1 className={classes.logo} data-testid="unsubscribe-title">
            <img src={logo} alt={intl.formatMessage({ id: 'unsubscribe.title' })} />
          </h1>
          <p className={cx(classes.font, classes.first)}>{intl.formatMessage(
            { id: 'unsubscribe.text-1' },
            { noBreak: <>&nbsp;</> },
          )}</p>
          <img className={classes.fontItalic} src={littleMail} alt={intl.formatMessage({ id: 'unsubscribe.text-2' })} />
          <p className={cx(classes.font, classes.second)}>{intl.formatMessage(
            { id: 'unsubscribe.text-3' },
            { noBreak: <>&nbsp;</> },
          )}</p>
          <p className={cx(classes.font, classes.grow)}><FormattedMessage id="unsubscribe.text-4" /></p>
          <h2 className={cx(classes.font, classes.subtitle)}><FormattedMessage id="unsubscribe.subtitle" /></h2>
        </div>
      </div>
      <Form ref={formRef} />
      <div className={classes.buttons}>
        <Button
          type="submit"
          style="dark"
          testId="cancel-unsubscribe"
          onClick={onCancel}
        ><FormattedMessage id="unsubscribe.btn-cancel" /></Button>
        <Button
          type="submit"
          style="outlined"
          testId="unsubscribe"
          onClick={onUnsubscribe}
        ><FormattedMessage id="unsubscribe.btn" /></Button>
      </div>
      <div className={classes.footerWrapper}>
        <div className={classes.phone}></div>
        <div className={classes.footer}>
          <p className={classes.font}>{intl.formatMessage(
            { id: 'unsubscribe.footer-1' },
            {
              noBreak: <>&nbsp;</>,
              sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
              a: (chunks: ReactNode) => <a href={ExternalLinks.mobileMarket}>{chunks}</a>,
              span: (chunks: ReactNode) => <span className={classes.link}>{chunks}</span>,
            },
          )}</p>
          <p className={classes.font}>{intl.formatMessage(
            { id: 'unsubscribe.footer-2' },
            { noBreak: <>&nbsp;</> },
          )}</p>
          <p className={classes.disclaimer}>
          <span>{intl.formatMessage(
            { id: 'unsubscribe.disclaimer-1' },
            { noBreak: <>&nbsp;</>, sup: (chunks: ReactNode) => <sup>{chunks}</sup> },
          )}</span>
            <span>{intl.formatMessage(
              { id: 'unsubscribe.disclaimer-2' },
              { noBreak: <>&nbsp;</>, sup: (chunks: ReactNode) => <sup>{chunks}</sup> },
            )}</span>
          </p>
        </div>
      </div>
    </Page>
  );
};
